import React, { FC, useCallback } from 'react'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Text } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { useUser } from '@/services/UserService'
import { getStringFromLocalStorage, removeFromLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { RESERVATION_REFUND_ERROR_RESERVATION_ID } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useModalRefundErrorHandler'
import { createZendeskLink } from '@/views/TicketCheckoutViews/utils/errorTrackingUtils'

interface Props {
  isOpen: boolean
  onClose: () => void
}
export const ErrorRefundedModal: FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslate('tickets')
  const { push } = useSafeRouter()
  const { user } = useUser()

  const onContactSupportClick = useCallback(() => {
    const reservationId = getStringFromLocalStorage(RESERVATION_REFUND_ERROR_RESERVATION_ID) ?? 'unknown'
    removeFromLocalStorage(RESERVATION_REFUND_ERROR_RESERVATION_ID)
    push(createZendeskLink({ reservationId, email: user?.email ?? '' }))
  }, [push, user?.email])

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={isOpen}
      onClose={onClose}
      className="z-50 mx-auto"
      childWrapperClassName="!m-0 !p-0"
      panelClasses="bg-white !max-w-[361px] md:!max-w-[600px] !w-full !rounded-2xl !p-8 !pt-10 md:!p-10 md:!pt-12 relative"
      transition="appear"
    >
      <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-core-gray-200 p-1" onClick={onClose}>
        <CloseIcon color="black" size={18} />
      </div>
      <div className="flex flex-col">
        <Text className="photon-title-lg md:photon-heading-xs" weight="bold">
          {t('reservationFailed', 'Reservation Failed')}
        </Text>

        <Text color="core-gray-700" className="photon-paragraph-sm md:photon-paragraph-md mt-4">
          {t(
            'reservationRefundedDescriptionSupport',
            'We weren’t able to complete your ticket reservation, but your payment is being refunded. If you have any questions or issues, feel free to reach out to our support team.',
          )}
        </Text>

        <div className="mt-8 flex flex-col gap-3 md:mr-auto md:mt-10 md:flex-row md:items-center">
          <Button onClick={onClose} variant="black">
            {t('tryAgainShowtimes', 'Try Again')}
          </Button>
          <Button onClick={onContactSupportClick} variant="black" outline>
            {t('contactCustomerSupport', 'Contact Customer Support')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
